// src/utils/api.ts
import { useAuth } from '../context/AuthContext';

// Get API base URL from environment variable or default to production backend
// Since environment variables may not be available at build time in Railway,
// we default to production backend URL
// Note: React embeds env vars at build time, so we need to ensure a valid fallback
const DEFAULT_API_URL = 'https://neighbohood-production.up.railway.app';
// Frontend URL for payment redirects
export const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || 'https://neighborhoodpulse.up.railway.app';

// Function to safely get API base URL
// This ensures we ALWAYS return a valid URL, never an empty string
const getApiBaseUrl = (): string => {
  // Get environment variable (may be undefined, null, or empty string at build time)
  // Webpack's DefinePlugin replaces process.env.REACT_APP_API_URL with the actual value
  // or undefined if not set
  const envUrl = process.env.REACT_APP_API_URL;
  
  // Convert to string and check if it's a valid non-empty URL
  const envUrlStr = envUrl ? String(envUrl).trim() : '';
  
  // Debug logging (only in development or if needed)
  if (process.env.NODE_ENV === 'development' || !envUrlStr) {
    console.log('[API Config] REACT_APP_API_URL from process.env:', envUrl);
    console.log('[API Config] Type of envUrl:', typeof envUrl);
    console.log('[API Config] EnvUrl string value:', envUrlStr);
  }
  
  // Check if env var is a valid non-empty URL string
  // Must start with http:// or https://
  if (envUrlStr && envUrlStr.length > 0 && (envUrlStr.startsWith('http://') || envUrlStr.startsWith('https://'))) {
    if (process.env.NODE_ENV === 'development' || !envUrlStr) {
      console.log('[API Config] Using environment variable:', envUrlStr);
    }
    return envUrlStr;
  }
  
  // Always fallback to production backend if env var is missing or invalid
  if (process.env.NODE_ENV === 'development' || !envUrlStr) {
    console.log('[API Config] Using default fallback URL:', DEFAULT_API_URL);
    console.warn('[API Config] REACT_APP_API_URL is not set or invalid. Using default:', DEFAULT_API_URL);
  }
  return DEFAULT_API_URL;
};

// Get the API base URL - this is evaluated at module load time
let API_BASE_URL = getApiBaseUrl();

// Validate that we have a valid URL (safety check)
// If somehow we got an invalid value, force the fallback
if (!API_BASE_URL || API_BASE_URL.trim() === '' || (!API_BASE_URL.startsWith('http://') && !API_BASE_URL.startsWith('https://'))) {
  console.error('[API Config] ERROR: API_BASE_URL is invalid! Using fallback.');
  API_BASE_URL = DEFAULT_API_URL;
  console.log('[API Config] Final API_BASE_URL (after validation):', API_BASE_URL);
} else {
  // Log the final value for debugging
  console.log('[API Config] Final API_BASE_URL:', API_BASE_URL);
}

// API utility function that handles token expiration automatically
export const apiRequest = async (
  url: string, 
  options: RequestInit = {}, 
  logout?: () => void
): Promise<Response> => {
  const token = localStorage.getItem('token');
  
  // If url doesn't start with http, prepend the API base URL
  const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
  
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(fullUrl, {
    ...options,
    headers,
  });

  // If we get a 401, the token is likely expired
  if (response.status === 401 && logout) {
    console.log('API request returned 401, token likely expired');
    logout();
  }

  return response;
};

// Hook for making authenticated API requests
export const useApiRequest = () => {
  const { logout } = useAuth();
  
  return (url: string, options: RequestInit = {}) => 
    apiRequest(url, options, logout);
};

// Export the API base URL for direct use
export { API_BASE_URL };



